import { Autocomplete } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import PmiTextField from 'shell/components/shared/text-field';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { ItemVM } from 'shell/view-models/item';
import { CohabitationNameItemVM } from 'shell/view-models/cohabitation-name';

interface CohabitationNameAutoCompleteProps {
  disabled?: boolean;
  value?: CohabitationNameItemVM | null;
  onChange: (item: CohabitationNameItemVM | null) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
}

interface CohabitationNameModel {
  numeroInterno: string;
  tipo: string;
  esponenteCivico: string;
  nome: string;
  via: string;
  descrizioneToponimo: string;
  key: {
    ente: string;
    codice: string;
  };
}

const CohabitationNameAutoCompleteComponent = ({
  disabled,
  onChange,
  required,
  value: selectedValue,
  error,
  label = 'Nome convivenza',
  id = 'cohabitationName',
}: CohabitationNameAutoCompleteProps): React.ReactElement => {
  const [cohabitations, getCohabitations] = useHttpFetch<
    HttpResponseList<CohabitationNameModel>,
    Array<ItemVM>
  >(`${__PERSON_API_URL__}/persona/convivenza/ricerca-nome`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<CohabitationNameModel>>) => {
      const { data } = response;

      return data?.payload.list.map((item) => ({
        id: item.key?.codice,
        value: item.nome,
        street: item.descrizioneToponimo,
      })) as Array<CohabitationNameItemVM>;
    },
  });

  const defaultProps = useMemo(
    () => ({
      options: (cohabitations.data as Array<CohabitationNameItemVM>) || [],
      getOptionLabel: (option: CohabitationNameItemVM) =>
        `${option.value} ${option.street ? `(${option.street})` : ''}`,
    }),
    [cohabitations.data]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = cohabitations;

        abort();

        getCohabitations({
          url: `/${value}`,
        });
      },
      [cohabitations.abort, getCohabitations]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={cohabitations.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

CohabitationNameAutoCompleteComponent.displayName = 'Cohabitation Name auto complete component';

const CohabitationNameAutoComplete = memo(CohabitationNameAutoCompleteComponent);

export default CohabitationNameAutoComplete as typeof CohabitationNameAutoCompleteComponent;
